import { ContentState, convertToRaw, EditorState } from "draft-js";
import htmlToDraft from "html-to-draftjs";
import { useState, useEffect } from "react";
import fakeDispatch from "../../../utils/fakeDispatch";
import useAdminDataStore, { selectCurrentGroupId, selectAllGroups } from "../../../store/adminData/adminData";
import { db } from "../../../services/firebaseService/connection";
import draftToHtml from "draftjs-to-html";
import { startUpdateUser } from "../../../redux/actions/user";
import { uploadImage } from "../../../services/firebaseService/endPoints/admin/members";
import moment from "moment";
import { doc, getDoc, updateDoc } from "@firebase/firestore";
import logger from "../../../utils/logger";
import { ChantButton, DescriptionText } from "../../../CommonComponents";
import { invalidateStadiumCache } from "../../../helperFunctions/util";
import ChantSelect from "../../../CommonComponents/ChantSelect/ChantSelect";
import { Divider } from "antd";

const Restricted = ({ value }) => {
  return (
    <div
      style={{
        padding: "8px 12px",
        borderRadius: "6px",
        backgroundColor: "#f5f5f5",
        border: "1px solid #d9d9d9",
        color: "#8c8c8c",
        fontSize: "14px",
        fontWeight: "500",
        cursor: "not-allowed",
        display: "flex",
        alignItems: "center",
      }}
    >
      {value}
    </div>
  );
};

const PermRow = ({ label, control, description }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: "16px", marginBottom: "12px" }}>
    <div style={{ width: "210px", flexShrink: 0, fontWeight: 500, paddingTop: "6px" }}>{label}</div>
    <div style={{ width: "200px", flexShrink: 0 }}>{control}</div>
    <div style={{ flex: 1, fontSize: 14, color: "#595959", paddingTop: "6px" }}>{description}</div>
  </div>
);

const FanPermission = (props) => {
  const currentGroup = useAdminDataStore(selectCurrentGroupId);
  const allGroups = useAdminDataStore(selectAllGroups);
  const adminData = allGroups[currentGroup];

  const [groupOverview, setGroupOverview] = useState(EditorState.createEmpty());
  const [groupPhoto, setGroupPhoto] = useState(null);
  const [permissions, setPermissions] = useState({
    matchPrediction: null,
    playerOfTheMatch: null,
    varPoll: null,
    gallery: null,
    groupChatStream: null,
  });
  const [republishWelcomeCards, setRepublishWelcomeCards] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const changeCheck = (e) => {
    if (e.target.id === "republishWelcomeCards") setRepublishWelcomeCards(e.target.checked);
  }; // eslint-disable-line no-unused-vars

  const changeSelect = (name, value) => {
    setPermissions((prev) => ({ ...prev, [name]: value }));
  };

  const getSelectValue = (value) => {
    if (value == null || value === "" || value === "Access.none") {
      return undefined;
    }
    return value;
  };

  const handleEditorStateChange = (state, name) => {
    if (name === "groupOverview") setGroupOverview(state);
  }; // eslint-disable-line no-unused-vars

  const fetchData = () => {
    setIsLoading(true);
    getDoc(doc(db, "group", currentGroup))
      .then((docSnap) => {
        if (docSnap.exists()) {
          let data = docSnap.data() || {};
          let perm = data.groupPermission || {
            matchPrediction: "",
            playerOfTheMatch: "",
            varPoll: "",
            gallery: "",
            groupChatStream: "",
          };
          let photo = data.groupPhoto || "";
          let content = htmlToDraft(data.groupOverview || "");
          let overview = EditorState.createEmpty();
          if (content) {
            let contentState = ContentState.createFromBlockArray(
              content.contentBlocks,
            );
            overview = EditorState.createWithContent(contentState);
          }
          setPermissions(perm);
          setGroupPhoto(photo);
          setGroupOverview(overview);
          setIsSaving(false);
          setIsLoading(false);
          logger.log({ permissions: perm, groupPhoto: photo, groupOverview: overview });
        } else {
          setIsLoading(false);
        }
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  const saveToFirebase = async () => {
    setIsSaving(true);
    let groupPermission = permissions;
    let groupOverviewHtml = draftToHtml(
      convertToRaw(groupOverview.getCurrentContent()),
    );
    let photo = "";
    if (
      groupPhoto &&
      typeof groupPhoto === "object" &&
      !groupPhoto.url
    ) {
      let path = `${adminData.data.groupName}/fanCardGroupPhoto`;
      await uploadImage(groupPhoto, path)
        .then(({ downloadUrl }) => downloadUrl)
        .then((downloadUrl) => {
          photo = downloadUrl;
        });
    } else {
      photo = groupPhoto || "";
    }
    let updateObj = {
      groupOverview: groupOverviewHtml,
      groupPermission,
      groupPhoto: photo,
    };
    if (republishWelcomeCards) {
      updateObj.welcomeCardRepublishTimestampInSec = moment().unix();
    }
    await updateDoc(doc(db, "group", currentGroup), updateObj);
    invalidateStadiumCache(adminData?.data?.groupKey);
    fakeDispatch(startUpdateUser());
    fetchData();
  };

  useEffect(() => {
    fetchData();
  }, [currentGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  const allowRestrictOptions = [
    { value: "full", label: "Allow" },
    { value: "none", label: "Restrict" },
  ];

  return (
    <div>
      <DescriptionText showBackground>
        Groups can provide non-members limited app access. Enable via Onboard
        (Option #4) and access your web link / QR code there. Set fan
        permissions below.
      </DescriptionText>

      <div style={{ marginTop: "2px", backgroundColor: "white", padding: "16px" }}>

        {/* Create Fan Welcome Card */}
        <p style={{ fontSize: 18, fontWeight: "bold", marginBottom: "8px" }}>
          Create Fan Welcome Card
        </p>
        <p style={{ color: "#525252" }}>
          A welcome card will show for Fans in the home feed of the app.
          Think of this as an ad – be brief and call them to action. You can
          share a link to your registration webpage (Join Now!). The card
          appears until the fan clicks &apos;x&apos; to close.
        </p>
        <p style={{ color: "#525252" }}>
          To create a welcome card first submit the form below then go to
          the Content / Welcome section of the dashboard.
        </p>
        <ChantButton onClick={() => props.goToPath(`/admin/welcome`)}>
          Create card
        </ChantButton>

        <Divider style={{ margin: "20px 0" }} />

        {/* Permissions */}
        <p style={{ fontSize: 22, fontWeight: "bold", marginBottom: "16px" }}>Permissions</p>

        <p style={{ fontSize: 18, fontWeight: "bold", marginBottom: "12px" }}>Set by Feature</p>
        <PermRow
          label="Match Predictions (enter)"
          control={
            <ChantSelect
              placeholder="Select option"
              options={allowRestrictOptions}
              value={getSelectValue(permissions.matchPrediction)}
              onChange={(value) => changeSelect("matchPrediction", value)}
              disabled={isLoading}
              loading={isLoading}
            />
          }
          description="Predictions card is visible to all. Set fan's ability to enter. Fans not eligible to win a prediction giveaway."
        />
        <PermRow
          label="Player of the Match (vote)"
          control={
            <ChantSelect
              placeholder="Select option"
              options={allowRestrictOptions}
              value={getSelectValue(permissions.playerOfTheMatch)}
              onChange={(value) => changeSelect("playerOfTheMatch", value)}
              disabled={isLoading}
              loading={isLoading}
            />
          }
          description="Player of match is visible to all. Set fan's ability to vote."
        />
        <PermRow
          label="VAR Poll (vote/comment)"
          control={
            <ChantSelect
              placeholder="Select option"
              options={allowRestrictOptions}
              value={getSelectValue(permissions.varPoll)}
              onChange={(value) => changeSelect("varPoll", value)}
              disabled={isLoading}
              loading={isLoading}
            />
          }
          description="VAR Poll is visible to all. Sets fan's ability to vote/comment."
        />
        <PermRow
          label="Gallery (Add/Download photos)"
          control={
            <ChantSelect
              placeholder="Select option"
              options={allowRestrictOptions}
              value={getSelectValue(permissions.gallery)}
              onChange={(value) => changeSelect("gallery", value)}
              disabled={isLoading}
              loading={isLoading}
            />
          }
          description="Set permission for fans to upload photos to gallery or view only (no share/download)."
        />
        <PermRow
          label="Chat Stream"
          control={
            <ChantSelect
              placeholder="Select option"
              options={[
                { value: "full", label: "Full Access" },
                { value: "partial", label: "React Only" },
                { value: "none", label: "Restrict" },
              ]}
              value={getSelectValue(permissions.groupChatStream)}
              onChange={(value) => changeSelect("groupChatStream", value)}
              disabled={isLoading}
              loading={isLoading}
            />
          }
          description="Set fan's permission to post, react only or fully restrict access. If able to post, group is responsible for monitoring behavior and adhering to terms of use."
        />

        <p style={{ fontSize: 18, fontWeight: "bold", margin: "16px 0 12px" }}>Set per Item</p>
        <PermRow
          label="Poll (view/answer)"
          control={<Restricted value="Set per poll via 'Add Poll'" />}
          description="Polls restricted to members will not be visible to fans."
        />
        <PermRow
          label="Giveaway (View / Enter)"
          control={<Restricted value="Set per giveaway via 'Add Giveaway'" />}
          description="All giveaways are visible to fans. Sets fan's ability to enter."
        />
        <PermRow
          label="Content (View / RSVP)"
          control={<Restricted value="Set per news/update via 'Add News/Event'" />}
          description="Set each time you create a new piece of content (watch party, news, etc). Fans can only see the RSVP 'count', not member details."
        />
        <PermRow
          label="Buy Group Tickets"
          control={<Restricted value="Set per ticket via 'Create Group Ticket'" />}
          description="Group tickets can be viewed by fans but only purchased if allowed by group."
        />
        <PermRow
          label="Buy Individual Tickets"
          control={<Restricted value="Set per ticket by member" />}
          description="Members have the option (per ticket post) to offer ticket purchase to fans."
        />

        <p style={{ fontSize: 18, fontWeight: "bold", margin: "16px 0 12px" }}>Set by Chant</p>
        <PermRow
          label="Access Channels / DM"
          control={<Restricted value="Restricted by Chant" />}
          description="Fans are not allowed to access Channels or DMs."
        />
        <PermRow
          label="View Membership card"
          control={<Restricted value="Restricted by Chant" />}
          description="Member cards are not offered for fans."
        />
        <PermRow
          label="Profile (Create / Edit)"
          control={<Restricted value="Restricted by Chant" />}
          description="Fan profiles will not appear in the Member List. Fans can add a profile photo, birthday and favorite player/moment. They will not see 'member since'. 'Favorite photos' will not be added to Gallery."
        />
        <PermRow
          label="Cap Me / 'Who's Here'"
          control={<Restricted value="Restricted by Chant" />}
          description="Cap Me is allowed. Viewing 'Who's Here' is restricted to members."
        />

        <Divider style={{ margin: "20px 0" }} />

        <div className="flex-center">
          <ChantButton disabled={isSaving} loading={isSaving} onClick={saveToFirebase}>
            Save
          </ChantButton>
        </div>
      </div>
    </div>
  );
};

export default FanPermission;
